import {ChangeEvent, useCallback, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {object, string} from 'yup';

import {
  BirthSex,
  MeasurementUnit,
  useRequestEmailChange,
  useUpdateContactDetails,
  useUpdateUserProfile,
} from '#apollo/schema';
import {useChangePassword} from '#component/page/ChangePassword/useChangePassword';
import {Pronoun} from '#constant/prounouns';
import {useAuth, useToastQueue} from '#hook';
import {
  convertKilogramToPound,
  convertPoundToKilogram,
  handleValidationError,
  heightValidator,
  measurementUnitRequiredValidator,
  minimumAgeValidator,
  numberValidator,
  passwordValidator,
  phoneValidator,
  stringValidator,
} from '#util';
import {convertCmToFeetString, convertFeetStringToCm} from '#util/height';
import {ValidationError} from '#util/validator/handleValidationError';

import {hasModifiedFields} from './FormAccountEdit.util';

import {FormAccountEditFields} from './FormAccountEdit.types';

export const useFormAccountEdit = (formData: FormAccountEditFields) => {
  const {addToast} = useToastQueue();
  const {
    firstName,
    lastName,
    email,
    birthday,
    birthSex,
    phoneNumber,
    weight,
    weightDisplayUnit,
    height,
    heightDisplayUnit,
    pronouns,
  } = formData;

  const {signInLoading} = useAuth();
  const [isOpenEmail, setIsOpenEmail] = useState(false);
  const [requestEmailChange] = useRequestEmailChange();
  const [updatePhoneNumber] = useUpdateContactDetails();
  const {handleSubmitUserPassword, loading} = useChangePassword();
  const [updateUserProfile] = useUpdateUserProfile();
  const [showResendMsg, setShowResendMsg] = useState(false);
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});
  const [isHeightMetric, setIsHeightMetric] = useState(
    heightDisplayUnit === MeasurementUnit.Metric,
  );
  const [isWeightMetric, setIsWeightMetric] = useState(
    weightDisplayUnit === MeasurementUnit.Metric,
  );
  const [showBirthSexMessage, setShowBirthSexMessage] = useState(birthSex === BirthSex.Unknown);

  const currentEmail = email;
  const currentPhoneNumber = phoneNumber;

  const {
    clearErrors,
    formState: {errors, isDirty, isSubmitting},
    handleSubmit: hookFormHandleSubmit,
    getFieldState,
    register,
    setError,
    setValue,
    getValues,
  } = useForm<FormAccountEditFields>({
    defaultValues: {
      birthSex: birthSex || BirthSex.Unknown,
      birthday: birthday || '',
      firstName: firstName || '',
      email: email || '',
      phoneNumber: phoneNumber || '',
      form: '',
      height: (isHeightMetric ? height : convertCmToFeetString(height as number)) || '', // height is always saved in metric units
      heightDisplayUnit: heightDisplayUnit || MeasurementUnit.Imperial,
      lastName: lastName || '',
      pronouns: pronouns || Pronoun.Unknown,
      weight: (isWeightMetric ? weight : convertKilogramToPound(weight as number)) || 0, // weight is always saved in metric units
      weightDisplayUnit: weightDisplayUnit || MeasurementUnit.Imperial,
    },
    mode: 'onBlur',
    resolver: yupResolver(
      object({
        firstName: stringValidator.required('Please enter your first name'),
        lastName: string().required(),
        email: string().required(),
        phoneNumber: phoneValidator.required('Please enter your phone number'),
        birthday: minimumAgeValidator.required('Please enter your birthday'),
        weightDisplayUnit: measurementUnitRequiredValidator,
        weight: numberValidator.nullable() || null,
        height: heightValidator().nullable(),
        heightDisplayUnit: measurementUnitRequiredValidator,
        pronouns: string().nullable(),
        currentPassword: stringValidator.nullable(),
        newPassword: string()
          .nullable()
          .test('min', 'Enter a valid Password', value => {
            if (!value) return true;
            return passwordValidator.isValidSync(value);
          }),
      }),
    ),
  });

  const onChangeHeightDisplayUnit = useCallback(
    (value: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => {
      const height = getValues('height');
      const heightDisplayUnitChanged = value.target.value as MeasurementUnit;
      if (heightDisplayUnitChanged === MeasurementUnit.Metric) {
        if (height && typeof height === 'number') return;
        const convertedHeightCm = convertFeetStringToCm(height as string);
        setValue('height', convertedHeightCm);
      } else {
        if (height && typeof height === 'string') return;
        const convertedHeightFeet = convertCmToFeetString(height as number);
        setValue('height', convertedHeightFeet);
      }
      setIsHeightMetric(heightDisplayUnitChanged === MeasurementUnit.Metric);
      setValue('heightDisplayUnit', heightDisplayUnitChanged);
      setTouchedFields(prev => ({...prev, heightDisplayUnit: true}));
    },
    [setValue, getValues],
  );

  const onChangeWeightDisplayUnit = useCallback(
    (value: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => {
      const weightDisplayUnitChanged = value.target.value as MeasurementUnit;
      setIsWeightMetric(weightDisplayUnitChanged === MeasurementUnit.Metric);
      setValue('weightDisplayUnit', weightDisplayUnitChanged);
      setTouchedFields(prev => ({...prev, weightDisplayUnit: true}));
      // There's a bug where this callback is called twice depending on where the user clicks
      // on the input field. This is a workaround to ensure the weight is reset to 0 when the unit is changed
      setValue('weight', 0);
    },
    [getValues, setValue],
  );

  const onChangeBirthSex = useCallback(
    (value: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => {
      setShowBirthSexMessage(value.target.value === BirthSex.Unknown);
      setTouchedFields(prev => ({...prev, birthSex: true}));
    },
    [],
  );

  const onChangePronouns = useCallback(
    (value: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => {
      setTouchedFields(prev => ({...prev, pronouns: true}));
    },
    [],
  );

  const onChangePhoneNumber = useCallback((value: ChangeEvent<HTMLInputElement>) => {
    setTouchedFields(prev => ({...prev, phoneNumber: true}));
  }, []);

  const onChangeEmail = useCallback((value: ChangeEvent<HTMLInputElement>) => {
    setTouchedFields(prev => ({...prev, email: true}));
  }, []);

  const handleResendEmail = async () => {
    try {
      const newEmail = getValues('email');
      if (newEmail && newEmail.toLowerCase() !== currentEmail.toLowerCase()) {
        await requestEmailChange({variables: {input: {newEmail}}});
        setIsOpenEmail(true);
      }
    } catch (error) {
      setError('email', {message: 'There was an issue resending your email'});
    }
  };

  const handleSubmit: SubmitHandler<FormAccountEditFields> = async ({
    email,
    firstName,
    lastName,
    birthday,
    birthSex,
    currentPassword,
    newPassword,
    phoneNumber,
    weight,
    height,
    pronouns,
    heightDisplayUnit,
    weightDisplayUnit,
  }) => {
    try {
      // Initiate email change
      const emailUpdated =
        email.toLowerCase() !== currentEmail.toLowerCase() && touchedFields?.email;
      if (emailUpdated) {
        await requestEmailChange({variables: {input: {newEmail: email}}});
        setShowResendMsg(true);
        setIsOpenEmail(true);
        setValue('email', email);
      }

      // Update phone number
      if (phoneNumber !== currentPhoneNumber && touchedFields?.phoneNumber) {
        await updatePhoneNumber({
          variables: {
            input: {
              phoneNumber,
            },
          },
        });
      }
      if (currentPassword && newPassword) {
        await handleSubmitUserPassword({currentPassword, newPassword});
      }

      // Update user profile
      const parsedWeight = isWeightMetric ? weight : convertPoundToKilogram(weight);
      const parsedHeight = isHeightMetric
        ? (height as number)
        : convertFeetStringToCm(height as string);

      const modifiedFields = {
        firstName: getFieldState('firstName')?.isTouched && !!firstName,
        lastName: getFieldState('lastName')?.isTouched && !!lastName,
        birthday: getFieldState('birthday')?.isTouched && !!birthday,
        birthSex: touchedFields?.birthSex && !!birthSex,
        pronouns: touchedFields?.pronouns && !!pronouns,
        weight: getFieldState('weight')?.isTouched && !!parsedWeight,
        weightDisplayUnit: touchedFields?.weightDisplayUnit && !!weightDisplayUnit,
        height: getFieldState('height')?.isTouched && !!parsedHeight,
        heightDisplayUnit: touchedFields?.heightDisplayUnit && !!heightDisplayUnit,
      };
      if (hasModifiedFields(modifiedFields)) {
        await updateUserProfile({
          variables: {
            input: {
              ...(modifiedFields.firstName && {firstName}),
              ...(modifiedFields.lastName && {lastName}),
              ...(modifiedFields.birthday && {birthday}),
              ...(modifiedFields.birthSex && {birthSex}),
              ...(modifiedFields.pronouns && {pronouns}),
              ...(modifiedFields.weight && {weight: parsedWeight}),
              ...(modifiedFields.weightDisplayUnit && {weightDisplayUnit}),
              ...(modifiedFields.height && {height: parsedHeight}),
              ...(modifiedFields.heightDisplayUnit && {heightDisplayUnit}),
            },
          },
        });
      }

      if (currentPassword && newPassword) {
        return;
      }

      if (isDirty) {
        addToast({children: 'Account updated'});
      } else {
        clearErrors();
        addToast({children: 'No changes were made'});
      }
    } catch (err) {
      const isInvalidPassword = (err as Error)?.message === 'Unauthorized';

      if (isInvalidPassword) {
        setError('currentPassword', {message: 'Current password is incorrect'});
        return;
      }
      handleValidationError({
        error: err as unknown as ValidationError<FormAccountEditFields>,
        errorMessages: {
          phoneNumber: {INVALID: 'Please enter a valid phone number'},
          birthday: {INVALID: 'Please enter a valid birthdate'},
          email: {DUPLICATE: 'An account with this email already exists'},
          form: 'Unable to update account. Please check the entered data and try again',
          rateLimit: [
            "We're experiencing an unusually high volume of traffic at the moment,",
            'and were unable to update your account.',
            'We apologize for any inconvenience this may cause and thank you for your patience and understanding.',
            'Please try again in a few minutes.',
          ].join(' '),
        },
        setError,
      });
    }
  };

  return {
    errors,
    isSubmitting,
    isHeightMetric,
    showBirthSexMessage,
    showResendMsg,
    isOpenEmail,
    loadingPass: loading,
    signInLoading,
    currentEmail,
    register,
    handleSubmit,
    handleResendEmail,
    clearErrors,
    hookFormHandleSubmit,
    setIsOpenEmail,
    onChangeEmail,
    onChangeBirthSex,
    onChangePronouns,
    onChangePhoneNumber,
    onChangeHeightDisplayUnit,
    onChangeWeightDisplayUnit,
  };
};
